if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mobileMql = window.matchMedia('(max-width: 989px)');
        this.mobileContent = this.querySelector('.product-media-modal__content');
        this.mobileCounter = this.querySelector('[data-mobile-gallery-counter]');
        this.mobileCurrent = this.querySelector('[data-mobile-gallery-current]');
        this.mobileTotal = this.querySelector('[data-mobile-gallery-total]');
        this.mobilePointers = new Map();
        this.mobileHighResolutionCache = new Map();
        this.mobileActiveIndex = 0;
        this.mobileScale = 1;
        this.mobileTranslateX = 0;
        this.mobileTranslateY = 0;
        this.mobileGesture = null;
        this.mobileAnimationFrame = null;
        this.mobileViewerActive = false;

        this.onMobilePointerDown = this.onMobilePointerDown.bind(this);
        this.onMobilePointerMove = this.onMobilePointerMove.bind(this);
        this.onMobilePointerEnd = this.onMobilePointerEnd.bind(this);
        this.onMobileResize = this.onMobileResize.bind(this);

        this.mobileContent?.addEventListener('pointerdown', this.onMobilePointerDown);
        this.mobileContent?.addEventListener('pointermove', this.onMobilePointerMove);
        this.mobileContent?.addEventListener('pointerup', this.onMobilePointerEnd);
        this.mobileContent?.addEventListener('pointercancel', this.onMobilePointerEnd);
      }

      hide() {
        if (this.mobileViewerActive) this.syncMobileGallery();
        this.deactivateMobileViewer();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        const activeMedia = this.getOpenedMedia();
        if (this.mobileMql.matches && activeMedia?.matches('[data-mobile-gallery-image]')) {
          this.activateMobileViewer(activeMedia);
        } else {
          this.showActiveMedia();
        }
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      getOpenedMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        return mediaId ? this.querySelector(`[data-media-id="${mediaId}"]`) : null;
      }

      get mobileImages() {
        return Array.from(this.mobileContent?.querySelectorAll('[data-mobile-gallery-image]') || []);
      }

      get activeMobileImage() {
        return this.mobileImages[this.mobileActiveIndex] || null;
      }

      activateMobileViewer(activeMedia) {
        const images = this.mobileImages;
        const activeIndex = images.indexOf(activeMedia);
        if (!images.length || activeIndex < 0) {
          this.showActiveMedia();
          return;
        }

        this.mobileViewerActive = true;
        this.classList.add('product-media-modal--mobile-image-viewer');
        this.mobileActiveIndex = activeIndex;
        this.mobilePointers.clear();
        this.resetMobileTransform();
        this.setMobileActiveImage(activeIndex, 0, false);
        window.addEventListener('resize', this.onMobileResize);
      }

      deactivateMobileViewer() {
        if (!this.mobileViewerActive) return;

        this.mobileViewerActive = false;
        this.classList.remove(
          'product-media-modal--mobile-image-viewer',
          'product-media-modal--mobile-gallery-next',
          'product-media-modal--mobile-gallery-previous'
        );
        this.mobilePointers.clear();
        this.mobileGesture = null;
        this.resetMobileTransform();
        this.mobileImages.forEach((image) => image.classList.remove('active'));
        window.removeEventListener('resize', this.onMobileResize);
        if (this.mobileAnimationFrame) cancelAnimationFrame(this.mobileAnimationFrame);
        this.mobileAnimationFrame = null;
      }

      setMobileActiveImage(index, direction = 0, animate = true) {
        const images = this.mobileImages;
        if (!images.length) return;

        this.mobileActiveIndex = (index + images.length) % images.length;
        this.resetMobileTransform();
        images.forEach((image, imageIndex) => image.classList.toggle('active', imageIndex === this.mobileActiveIndex));

        this.classList.remove(
          'product-media-modal--mobile-gallery-next',
          'product-media-modal--mobile-gallery-previous'
        );
        if (animate && direction !== 0) {
          const directionClass =
            direction > 0
              ? 'product-media-modal--mobile-gallery-next'
              : 'product-media-modal--mobile-gallery-previous';
          this.classList.add(directionClass);
          window.setTimeout(() => this.classList.remove(directionClass), 220);
        }

        if (this.mobileCurrent) this.mobileCurrent.textContent = String(this.mobileActiveIndex + 1);
        if (this.mobileTotal) this.mobileTotal.textContent = String(images.length);
        if (this.mobileCounter) this.mobileCounter.hidden = false;

        this.loadMobileHighResolution(this.mobileActiveIndex, true);
        this.preloadAdjacentMobileImages();
      }

      resetMobileTransform() {
        this.mobileScale = 1;
        this.mobileTranslateX = 0;
        this.mobileTranslateY = 0;
        this.mobileGesture = null;
        this.applyMobileTransform(false);
      }

      getMobileMaxScale(image = this.activeMobileImage) {
        if (!image) return 1;
        const baseWidth = image.offsetWidth || image.getBoundingClientRect().width;
        const baseHeight = image.offsetHeight || image.getBoundingClientRect().height;
        const sourceWidth = Number(image.dataset.imageWidth) || image.naturalWidth;
        const sourceHeight = Number(image.dataset.imageHeight) || image.naturalHeight;
        if (!baseWidth || !baseHeight || !sourceWidth || !sourceHeight) return 1;

        const nativeScale = Math.min(sourceWidth / baseWidth, sourceHeight / baseHeight);
        return Math.max(1, Math.min(3, Math.floor(nativeScale * 10) / 10));
      }

      clampMobileTranslation() {
        const image = this.activeMobileImage;
        if (!image || !this.mobileContent) return;

        const baseWidth = image.offsetWidth || image.getBoundingClientRect().width;
        const baseHeight = image.offsetHeight || image.getBoundingClientRect().height;
        const maxX = Math.max(0, (baseWidth * this.mobileScale - this.mobileContent.clientWidth) / 2);
        const maxY = Math.max(0, (baseHeight * this.mobileScale - this.mobileContent.clientHeight) / 2);
        this.mobileTranslateX = Math.min(maxX, Math.max(-maxX, this.mobileTranslateX));
        this.mobileTranslateY = Math.min(maxY, Math.max(-maxY, this.mobileTranslateY));
      }

      applyMobileTransform(animate = false) {
        const image = this.activeMobileImage;
        if (!image) return;

        image.classList.toggle('is-mobile-gallery-settling', animate);
        image.style.transform = `translate3d(${this.mobileTranslateX.toFixed(2)}px, ${this.mobileTranslateY.toFixed(2)}px, 0) scale(${this.mobileScale.toFixed(3)})`;
        if (animate) window.setTimeout(() => image.classList.remove('is-mobile-gallery-settling'), 220);
      }

      queueMobileTransform() {
        if (this.mobileAnimationFrame) return;
        this.mobileAnimationFrame = requestAnimationFrame(() => {
          this.mobileAnimationFrame = null;
          this.applyMobileTransform(false);
        });
      }

      onMobilePointerDown(event) {
        if (!this.mobileViewerActive || (!event.isPrimary && event.pointerType === 'mouse')) return;
        event.preventDefault();
        event.stopPropagation();
        this.mobileContent.setPointerCapture?.(event.pointerId);
        this.mobilePointers.set(event.pointerId, this.mobilePointer(event));

        if (this.mobilePointers.size === 2) {
          const [first, second] = Array.from(this.mobilePointers.values());
          this.mobileGesture = {
            type: 'pinch',
            startDistance: this.mobilePointerDistance(first, second),
            startScale: this.mobileScale,
            startTranslateX: this.mobileTranslateX,
            startTranslateY: this.mobileTranslateY,
            startMidpoint: this.mobilePointerMidpoint(first, second),
          };
          return;
        }

        this.mobileGesture = {
          type: this.mobileScale > 1.01 ? 'pan' : 'swipe',
          startX: event.clientX,
          startY: event.clientY,
          lastX: event.clientX,
          lastTime: event.timeStamp,
          velocityX: 0,
          startTranslateX: this.mobileTranslateX,
          startTranslateY: this.mobileTranslateY,
        };
      }

      onMobilePointerMove(event) {
        if (!this.mobileViewerActive || !this.mobilePointers.has(event.pointerId)) return;
        event.preventDefault();
        event.stopPropagation();
        this.mobilePointers.set(event.pointerId, this.mobilePointer(event));

        if (this.mobilePointers.size >= 2) {
          const [first, second] = Array.from(this.mobilePointers.values());
          if (this.mobileGesture?.type !== 'pinch') {
            this.mobileGesture = {
              type: 'pinch',
              startDistance: this.mobilePointerDistance(first, second),
              startScale: this.mobileScale,
              startTranslateX: this.mobileTranslateX,
              startTranslateY: this.mobileTranslateY,
              startMidpoint: this.mobilePointerMidpoint(first, second),
            };
          }
          this.updateMobilePinch(first, second);
          return;
        }

        if (!this.mobileGesture || this.mobileGesture.type === 'pinch') return;
        const deltaX = event.clientX - this.mobileGesture.startX;
        const deltaY = event.clientY - this.mobileGesture.startY;
        const elapsed = Math.max(1, event.timeStamp - this.mobileGesture.lastTime);
        this.mobileGesture.velocityX = (event.clientX - this.mobileGesture.lastX) / elapsed;
        this.mobileGesture.lastX = event.clientX;
        this.mobileGesture.lastTime = event.timeStamp;

        if (this.mobileGesture.type === 'pan' || this.mobileScale > 1.01) {
          this.mobileGesture.type = 'pan';
          this.mobileTranslateX = this.mobileGesture.startTranslateX + deltaX;
          this.mobileTranslateY = this.mobileGesture.startTranslateY + deltaY;
          this.clampMobileTranslation();
        } else {
          this.mobileTranslateX = deltaX * 0.35;
          this.mobileTranslateY = 0;
        }
        this.queueMobileTransform();
      }

      onMobilePointerEnd(event) {
        if (!this.mobileViewerActive || !this.mobilePointers.has(event.pointerId)) return;
        event.preventDefault();
        event.stopPropagation();
        this.mobilePointers.delete(event.pointerId);

        if (this.mobilePointers.size > 0) {
          const remaining = Array.from(this.mobilePointers.values())[0];
          this.mobileGesture = {
            type: this.mobileScale > 1.01 ? 'pan' : 'swipe',
            startX: remaining.clientX,
            startY: remaining.clientY,
            lastX: remaining.clientX,
            lastTime: event.timeStamp,
            velocityX: 0,
            startTranslateX: this.mobileTranslateX,
            startTranslateY: this.mobileTranslateY,
          };
          return;
        }

        if (this.mobileGesture?.type === 'swipe' && this.mobileScale <= 1.01) {
          const rawDistance = event.clientX - this.mobileGesture.startX;
          const distanceThreshold = Math.max(54, this.mobileContent.clientWidth * 0.14);
          const velocityPassed =
            Math.abs(this.mobileGesture.velocityX) >= 0.45 && Math.abs(rawDistance) >= 24;
          if (Math.abs(rawDistance) >= distanceThreshold || velocityPassed) {
            this.setMobileActiveImage(
              this.mobileActiveIndex + (rawDistance < 0 ? 1 : -1),
              rawDistance < 0 ? 1 : -1
            );
            return;
          }
        }

        if (this.mobileScale <= 1.01) {
          this.mobileScale = 1;
          this.mobileTranslateX = 0;
          this.mobileTranslateY = 0;
        } else {
          this.clampMobileTranslation();
        }
        this.mobileGesture = null;
        this.applyMobileTransform(true);
      }

      updateMobilePinch(first, second) {
        const gesture = this.mobileGesture;
        if (!gesture?.startDistance) return;

        const maxScale = this.getMobileMaxScale();
        const nextScale = Math.min(
          maxScale,
          Math.max(1, gesture.startScale * (this.mobilePointerDistance(first, second) / gesture.startDistance))
        );
        const midpoint = this.mobilePointerMidpoint(first, second);
        const contentRect = this.mobileContent.getBoundingClientRect();
        const centerX = contentRect.left + contentRect.width / 2;
        const centerY = contentRect.top + contentRect.height / 2;
        const scaleRatio = nextScale / gesture.startScale;

        this.mobileTranslateX =
          midpoint.x - centerX - scaleRatio * (gesture.startMidpoint.x - centerX - gesture.startTranslateX);
        this.mobileTranslateY =
          midpoint.y - centerY - scaleRatio * (gesture.startMidpoint.y - centerY - gesture.startTranslateY);
        this.mobileScale = nextScale;

        if (this.mobileScale <= 1.01) {
          this.mobileScale = 1;
          this.mobileTranslateX = 0;
          this.mobileTranslateY = 0;
        } else {
          this.clampMobileTranslation();
        }
        this.queueMobileTransform();
      }

      mobilePointer(event) {
        return { clientX: event.clientX, clientY: event.clientY };
      }

      mobilePointerDistance(first, second) {
        return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
      }

      mobilePointerMidpoint(first, second) {
        return {
          x: (first.clientX + second.clientX) / 2,
          y: (first.clientY + second.clientY) / 2,
        };
      }

      onMobileResize() {
        if (!this.mobileViewerActive) return;
        if (!this.mobileMql.matches) {
          this.hide();
          return;
        }
        this.clampMobileTranslation();
        this.applyMobileTransform(false);
      }

      loadMobileHighResolution(index, applyToImage = false) {
        const image = this.mobileImages[index];
        const source = image?.dataset.mobileGallerySrc;
        if (!image || !source) return Promise.resolve();

        let loadPromise = this.mobileHighResolutionCache.get(source);
        if (!loadPromise) {
          loadPromise = new Promise((resolve, reject) => {
            const preloadImage = new Image();
            preloadImage.decoding = 'async';
            preloadImage.onload = () => resolve(source);
            preloadImage.onerror = reject;
            preloadImage.src = source;
          });
          this.mobileHighResolutionCache.set(source, loadPromise);
        }

        if (applyToImage) {
          loadPromise
            .then(() => {
              if (image.dataset.mobileGalleryLoaded === 'true') return;
              image.srcset = '';
              image.src = source;
              image.dataset.mobileGalleryLoaded = 'true';
            })
            .catch(() => {});
        }
        return loadPromise;
      }

      preloadAdjacentMobileImages() {
        const images = this.mobileImages;
        if (images.length < 2) return;
        const previousIndex = (this.mobileActiveIndex - 1 + images.length) % images.length;
        const nextIndex = (this.mobileActiveIndex + 1) % images.length;
        this.loadMobileHighResolution(previousIndex, false);
        if (nextIndex !== previousIndex) this.loadMobileHighResolution(nextIndex, false);
      }

      syncMobileGallery() {
        const image = this.activeMobileImage;
        if (!image) return;

        const sectionId = this.id.replace('ProductModal-', '');
        const gallery = document.getElementById(`MediaGallery-${sectionId}`);
        const mediaId = `${sectionId}-${image.dataset.mediaId}`;
        gallery?.setActiveMedia?.(mediaId, false, { playMedia: false });
      }
    }
  );
}
